import { LocationState, HotelLocationState, DateState, TravelersState } from "stores/wizard/state/global";
import { GlobalConfig } from "stores/wizard/config/staticConfigs/global";
import { observable, computed, action } from "mobx";
import { dateState } from "./date";
import { travelersState } from "./travelers";
import { locationState, hotelLocationState } from "./location";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { TypeaheadSelectionProps } from "src/components/flexComponents/WizardHotelPWA/typings";
import { Travelers, TravelersMetadata } from "src/components/shared/TravelersField/typings";
import { travelersMetadata } from "src/components/shared/TravelersField/utils";
import { TravelersFieldConfig, DateFieldConfig } from "stores/wizard/config";
import { InfantSeating } from "src/components/shared/TravelersField";
import { LastSearchData } from "stores/wizard/LastSearchData";
import { DeviceInformation } from "node_modules/bernie-context/dist/platform/device-information.d.ts";
import { ClassCodes } from "src/components/shared/PreferredClassInput/PreferredClassInput";
import { PreferredAirline } from "src/components/shared/PreferredAirlineInput/PreferredAirlineInput";
import { FlightType } from "../typings";

export class GlobalWizardState {
  @observable
  public formSubmitted: boolean = false;

  @observable
  public travelersValueChanged: boolean = false;

  @observable
  public datesValueChanged: boolean = false;

  @computed
  get isFormSubmitted() {
    return this.formSubmitted;
  }

  set isFormSubmitted(submitted: boolean) {
    this.formSubmitted = submitted;
  }

  @observable
  public location: LocationState;

  @observable
  public hotelLocation: HotelLocationState;

  @observable
  public config: GlobalConfig;

  @observable
  public canTrack?: boolean;

  public lastSearchData?: LastSearchData;

  @observable
  public isDesktop = false;

  @observable
  public navigation: {
    defaultLob: string;
    activeLob: string;
  };

  public useDefaultLOB = true;

  @observable
  public date: DateState;

  @observable
  public travelers: TravelersState;

  //initialize to economy
  @observable
  public flightClassCode: ClassCodes = ClassCodes.ECONOMY;

  @observable
  public flightAirline: PreferredAirline;

  @observable
  public flightTypeCode: FlightType = FlightType.ROUND_TRIP;

  /**
   * Helps to tell if the component should update data from last search cookie or not.
   */
  @observable
  public shouldUpdateCookie: boolean = true;

  public updateShouldUpdateFromCookie(shouldUpdate: boolean) {
    this.shouldUpdateCookie = shouldUpdate;
  }

  @computed
  public get hotelTravelersMetadata() {
    return travelersMetadata(this.travelers.hotel);
  }

  @computed
  public get hotelPackageTravelersMetadata() {
    return travelersMetadata(this.travelers.hotelPackage);
  }

  @computed
  public get nonHotelTravelersMetadata() {
    return travelersMetadata(this.travelers.nonHotel);
  }

  @computed
  public get cruiseTravelersMetadata() {
    return travelersMetadata(this.travelers.cruise);
  }

  @computed
  public get defaultLob() {
    return this.navigation.defaultLob;
  }

  @computed
  public get activeLob() {
    return this.navigation.activeLob;
  }

  @action
  public moreThanOneError = (numberOfErrors: number) => {
    return numberOfErrors > 1;
  };

  @action
  public setIsDesktop(deviceInformation?: DeviceInformation) {
    //default to false if device information is not available
    this.isDesktop = deviceInformation ? !(deviceInformation.tablet || deviceInformation.mobile) : false;
  }

  @action
  public setActiveLob(activeLob: string) {
    this.navigation.activeLob = activeLob;
  }

  @action
  public updateDateSelection = (start: Date, end: Date) => {
    this.date.start = start;
    this.date.end = end;
    this.datesValueChanged = true;
  };

  @action
  public updateOriginSelection(selection: TypeaheadSelection) {
    const { term, data } = selection as TypeaheadSelectionProps;
    this.location.origin.value = term;
    this.location.origin.metaData.destinationId = data?.cityId ? data?.cityId : data?.regionId;
    this.location.origin.metaData.regionId = data && data.regionId;
    this.location.origin.metaData.ttla = data?.hierarchyInfo?.airport.airportCode;
    this.location.origin.metaData.countryCode = data?.hierarchyInfo?.country?.isoCode2;
    this.location.origin.metaData.shortName = data?.regionNames?.shortName;
    this.location.origin.metaData.latLong =
      data?.location?.lat && data?.location?.long ? `${data.location.lat},${data.location.long}` : "";
  }

  @action
  public updateDestinationSelection(selection: TypeaheadSelection) {
    const { term, data } = selection as TypeaheadSelectionProps;
    this.location.destination.value = term;
    this.location.destination.metaData.destinationId = data?.cityId || data?.regionId;
    this.location.destination.metaData.regionId = data?.regionId;
    this.location.destination.metaData.ttla = data?.hierarchyInfo?.airport?.airportCode;
    this.location.destination.metaData.countryCode = data?.hierarchyInfo?.country?.isoCode2;
    this.location.destination.metaData.shortName = data?.regionNames?.shortName;
    this.location.destination.metaData.hotelId = data?.selected;
    this.location.destination.metaData.latLong =
      data?.location?.lat && data?.location?.long ? `${data.location.lat},${data.location.long}` : "";
  }

  @action
  public updateHotelDestinationSelection(selection: TypeaheadSelection) {
    const { term, data } = selection as TypeaheadSelectionProps;
    this.hotelLocation.destination.value = term;
    this.hotelLocation.destination.metaData.regionId = data?.regionId || data?.selected;
  }

  @action
  public swapOriginAndDestinationLocations() {
    const tempLocation = this.location.origin;
    this.location.origin = this.location.destination;
    this.location.destination = tempLocation;
  }

  public updateDestinationFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string,
    destinationID: string,
    hotelID: string,
    timestamp?: number
  ) {
    this.location.destination.value = value;
    this.location.destination.metaData.regionId = regionID;
    this.location.destination.metaData.ttla = ttla;
    this.location.destination.metaData.countryCode = countryCode;
    this.location.destination.metaData.latLong = latLong;
    this.location.destination.metaData.destinationId = destinationID;
    this.location.destination.metaData.hotelId = hotelID;
    if (!this.location.destination.metaData.lastSearchLatLong) {
      this.location.destination.metaData.lastSearchLatLong = latLong;
    }
    if (!this.location.destination.metaData.lastSearchTimestamp) {
      this.location.destination.metaData.lastSearchTimestamp = timestamp;
    }
  }

  public updateOriginFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string,
    timestamp: number
  ) {
    this.location.origin.value = value;
    this.location.origin.metaData.regionId = regionID;
    this.location.origin.metaData.ttla = ttla;
    this.location.origin.metaData.countryCode = countryCode;
    this.location.origin.metaData.latLong = latLong;
    if (!this.location.origin.metaData.lastSearchLatLong) {
      this.location.origin.metaData.lastSearchLatLong = latLong;
    }
    if (!this.location.origin.metaData.lastSearchTimestamp) {
      this.location.origin.metaData.lastSearchTimestamp = timestamp;
    }
  }

  public updateOriginFromContext(value: string, regionID: string, countryCode: string, latLong: string, ttla: string) {
    this.location.origin.value = value;
    this.location.origin.metaData.regionId = regionID;
    this.location.origin.metaData.ttla = ttla;
    this.location.origin.metaData.countryCode = countryCode;
    this.location.origin.metaData.latLong = latLong;
  }

  public updateDestinationFromContext(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string,
    destinationID: string,
    hotelID: string
  ) {
    this.location.destination.value = value;
    this.location.destination.metaData.regionId = regionID;
    this.location.destination.metaData.ttla = ttla;
    this.location.destination.metaData.countryCode = countryCode;
    this.location.destination.metaData.latLong = latLong;
    this.location.destination.metaData.destinationId = destinationID;
    this.location.destination.metaData.hotelId = hotelID;
  }

  public updateHotelDestinationFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string
  ) {
    this.hotelLocation.destination.value = value;
    this.hotelLocation.destination.metaData.regionId = regionID;
    this.hotelLocation.destination.metaData.ttla = ttla;
    this.hotelLocation.destination.metaData.countryCode = countryCode;
    this.hotelLocation.destination.metaData.latLong = latLong;
  }

  @action
  public updateHotelTravelersSelection = (travelers: Travelers) => {
    this.travelers.nonHotel.rooms = [travelers.rooms[0]];
    this.travelers.hotel.rooms = travelers.rooms;
    this.travelers.hotelPackage.rooms = travelers.rooms;
    this.travelers.cruise.rooms = [travelers.rooms[0]];

    this.travelers.nonHotel.infantSeating = travelers.infantSeating;
    this.travelers.hotel.infantSeating = travelers.infantSeating;
    this.travelers.hotelPackage.infantSeating = travelers.infantSeating;
    this.travelers.cruise.infantSeating = travelers.infantSeating;
    this.setTravelersValue();
  };

  @action
  public updateNonHotelTravelersSelection = (travelers: Travelers) => {
    this.travelers.nonHotel.rooms = [travelers.rooms[0]];
    this.travelers.hotel.rooms[0] = travelers.rooms[0];
    this.travelers.hotelPackage.rooms[0] = travelers.rooms[0];
    this.travelers.cruise.rooms = [travelers.rooms[0]];

    this.travelers.nonHotel.infantSeating = travelers.infantSeating;
    this.travelers.hotel.infantSeating = travelers.infantSeating;
    this.travelers.hotelPackage.infantSeating = travelers.infantSeating;
    this.travelers.cruise.infantSeating = travelers.infantSeating;
    this.setTravelersValue();
  };

  @action
  public setFlightClass(flightClassCode: ClassCodes) {
    this.flightClassCode = flightClassCode;
  }

  @action
  public setFlightAirline(flightAirline: PreferredAirline) {
    this.flightAirline = flightAirline;
  }

  @action
  public setFlightType(flightTypeCode: FlightType) {
    this.flightTypeCode = flightTypeCode;
  }

  @action
  public setTravelersValue = () => {
    this.travelersValueChanged = true;
  };

  @action
  public validateOriginField = (): boolean => !!this.location.origin.value;

  @action
  public validateDestinationField = (): boolean => !!this.location.destination.value;

  @action
  public submit() {
    this.formSubmitted = true;
  }

  public validateOriginDifferentFromDestination = (): boolean =>
    this.location.origin.value !== this.location.destination.value;

  public validateLessThanNTravelers = (
    { numOfAdults, numOfChildren, numOfInfants }: TravelersMetadata,
    { withInfants, minBookableTravelers, maxBookableTravelers }: TravelersFieldConfig
  ): boolean => {
    numOfAdults = numOfAdults === 0 ? 1 : numOfAdults;
    const numOfTravelers = numOfAdults + numOfChildren + (withInfants ? numOfInfants : 0);

    return numOfTravelers >= minBookableTravelers && numOfTravelers <= maxBookableTravelers;
  };

  public validateUnattendedInfantInLap = (
    { travelersAgeTwelveOrOlder, numOfInfants }: TravelersMetadata,
    { withInfants }: TravelersFieldConfig,
    infantSeating: InfantSeating
  ): boolean => {
    if (!withInfants || infantSeating === InfantSeating.IN_SEAT) {
      return true;
    }

    return travelersAgeTwelveOrOlder >= numOfInfants;
  };

  public validateInfantsPerTraveler = (
    { travelersAgeTwelveOrOlder, numOfInfants }: TravelersMetadata,
    { withInfants, minInfantPerTraveler }: TravelersFieldConfig,
    infantSeating: InfantSeating
  ): boolean => {
    if (!withInfants || infantSeating === InfantSeating.ON_LAP) {
      return true;
    }

    return travelersAgeTwelveOrOlder * minInfantPerTraveler! >= numOfInfants;
  };

  public validatePartialDateWithinRange = (rangeStart: Date, rangeEnd: Date, date: Date): boolean => {
    const minValidDate = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate());
    const maxValidDate = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate(), 23, 59, 59, 999);

    return date >= minValidDate && date <= maxValidDate;
  };

  public validateMaxDateRange = (startDate: Date, endDate: Date, maxDaysRange: number): boolean => {
    const maxEndDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + maxDaysRange + 1);

    return endDate < maxEndDate;
  };

  public setLastSearchedLob = () => {
    if (this.canTrack && this.config.useLastSearchForMultiLOB && this.lastSearchData && this.lastSearchData.lob) {
      this.navigation.defaultLob = this.lastSearchData.lob || this.config.navigation.defaultLob;
    }
  };

  public updateDateFromConfig = (dateConfig: DateFieldConfig) => {
    if (!this.datesValueChanged) {
      this.date = this.getDateFromConfig(dateConfig);
    }
  };

  public getDateFromConfig = (dateConfig: DateFieldConfig): DateState => {
    const { defaultOffsetDates } = dateConfig;
    if (defaultOffsetDates) {
      const { start, end } = defaultOffsetDates;

      return dateState(end, start);
    }

    return dateState();
  };

  constructor(config: GlobalConfig, canTrack?: boolean, lastSearchData?: LastSearchData) {
    this.config = config;
    this.navigation = config.navigation;
    this.location = locationState();
    this.hotelLocation = hotelLocationState();
    this.date = dateState();
    this.travelers = travelersState;
    this.canTrack = canTrack;
    if (this.canTrack && lastSearchData) {
      this.lastSearchData = lastSearchData;
    }
  }
}
